import { useMemo } from "react";
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import { BaseWidget } from "./BaseWidget";
import { PeriodOverrideControl } from "../controls/PeriodOverrideControl";
import { ColorControl } from "../controls/ColorControl";
import useWidgetPeriod from "./_shared/useWidgetPeriod";
import useSettings from "../../../../hooks/Settings";
import useSharedCollectionWhere from "../../../../firebase/useSharedCollectionWhere";
import HouseVisitsHoursVisualization from "../../../dataVisualizations/HouseVisitsHoursVisualization";
import { buildCumulativeHoursSeries } from "../../../../helpers/dateHelpers/adressDashboard/tasksHours";
import ModulePlaceholder from "./_shared/ModulePlaceholder";
import isTemplateMode from "./_shared/isTemplateMode";

// AI-CONTEXT: See docs/features/livereport-widgets-housevisits-personas.md
// HouseVisitsHoursWidget — cumulative-hours line chart over a date range
// for the Huishoudens module. Mirrors DashboardHouseVisitsHoursCard.


/**
 * Slice a cumulative-hours series to the configured date range. Recomputes
 * the cumulative total from the slice so the first point reflects the
 * starting baseline within the window, matching what users expect from a
 * "last 30 days" view.
 *
 * @param {Array<{day: string, hours: number}>} series - day keys are 'YYYY-MM-DD'
 * @param {{start: string|null, end: string|null}} range
 * @returns {Array<{day: string, hours: number, cumulativeHours: number}>}
 */
const sliceCumulativeSeries = (series, range) => {
    const arr = Array.isArray(series) ? series : [];
    const { start, end } = range || {};
    const inWindow = arr.filter((row) => {
        if (!row?.day) return false;
        if (start && row.day < start) return false;
        if (end && row.day > end) return false;
        return true;
    });
    let total = 0;
    return inWindow.map((row) => {
        total += row.hours || 0;
        return { day: row.day, hours: row.hours, cumulativeHours: total };
    });
};

/**
 * @extends {BaseWidget}
 */
export class HouseVisitsHoursWidget extends BaseWidget {

    getName() { return 'Gewerkte uren'; }
    getCategory() { return 'huishoudens'; }
    getRequiredModule() { return 'housevisits'; }
    getIcon() { return <TimerOutlinedIcon />; }

    getControls() {
        return {
            /* Migrated from DateRangeControl: same 'dateRange' key so legacy
               saved {start,end} values keep working (mode is derived). */
            'dateRange': new PeriodOverrideControl({
                label: 'Periode',
                description: 'Welke periode de cumulatieve telling toont.',
                defaultValue: { mode: 'inherit', start: null, end: null },
            }),
            'graphColor': new ColorControl({
                label: 'Lijnkleur',
                description: 'Kleur van de lijn. Leeg = gebruik de tenant-secundaire kleur.',
                defaultValue: '',
            }),
        };
    }

    render(props) {
        return <Renderer {...props} />;
    }
}

/**
 * @param {object} props
 * @param {{mode?: string, start: string|null, end: string|null}} [props.dateRange] - Widget period setting (PeriodOverrideControl; legacy values carry no mode)
 * @param {string} [props.graphColor]
 * @returns {JSX.Element}
 */
const Renderer = ({ dateRange, graphColor }) => {
    const { housevisits, SecundairyColor } = useSettings();
    const isTemplate = isTemplateMode();
    const tasks = useSharedCollectionWhere('Tasks', 'Type', 'addressVisit');
    const { range } = useWidgetPeriod(dateRange);

    const fullSeries = useMemo(() => buildCumulativeHoursSeries(tasks), [tasks]);
    const windowedSeries = useMemo(
        () => sliceCumulativeSeries(fullSeries, range),
        [fullSeries, range]
    );

    if (isTemplate) {
        return <ModulePlaceholder mode="template" />;
    }
    if (housevisits !== true) {
        return <ModulePlaceholder mode="disabled" moduleLabel="Huishoudens" />;
    }

    const color = graphColor || SecundairyColor;

    return (
        <div className="housevisits-widget-chart">
            <HouseVisitsHoursVisualization data={windowedSeries} color={color} height={'100%'} />
        </div>
    );
};

export { sliceCumulativeSeries };
